/**
 * Static renderer-side mirror of the importer descriptors, in menu order. The
 * command palette is built synchronously (can't await the async
 * `connection-import:list` IPC), so labels + file-only flags live here too. The
 * menu still merges live `detected` flags from `list()` onto this order. Keep
 * this in sync with `electron/connection-import/importerRegistry.ts` labels.
 */
import type { ImportSourceId } from '../../../types/connectionImport';

/** Where a source's connections come from, for the menu's groups (Option C final UX review UX-17). */
export type ImportSourceGroup = 'apps' | 'files' | 'azure';

export interface ImportSourceMeta {
  id: ImportSourceId;
  label: string;
  /** No auto-detect on any platform — the menu/palette jumps straight to Browse…. */
  fileOnly?: boolean;
  group: ImportSourceGroup;
}

export const IMPORT_SOURCES: ImportSourceMeta[] = [
  { id: 'jam', label: 'Jam SQL Studio export', fileOnly: true, group: 'files' },
  { id: 'azure', label: 'Azure (signed-in account)', group: 'azure' },
  { id: 'ads', label: 'Azure Data Studio', group: 'apps' },
  { id: 'vscode', label: 'VS Code (SQL extensions)', group: 'apps' },
  { id: 'ssms', label: 'SQL Server Management Studio', group: 'apps' },
  { id: 'dbeaver', label: 'DBeaver', group: 'apps' },
  { id: 'datagrip', label: 'DataGrip (JetBrains IDEs)', group: 'apps' },
  { id: 'tableplus', label: 'TablePlus', group: 'apps' },
  { id: 'mysql-workbench', label: 'MySQL Workbench', group: 'apps' },
  { id: 'sqldeveloper', label: 'Oracle SQL Developer', group: 'apps' },
  { id: 'navicat', label: 'Navicat (.ncx export)', fileOnly: true, group: 'files' },
  { id: 'pgadmin', label: 'pgAdmin (servers.json export)', fileOnly: true, group: 'files' },
  { id: 'tnsnames', label: 'Oracle tnsnames.ora', group: 'files' },
  { id: 'pg-service', label: 'PostgreSQL service file (pg_service.conf)', group: 'files' },
  { id: 'my-cnf', label: 'MySQL option file (.my.cnf)', group: 'files' },
  { id: 'odbc', label: 'ODBC Data Sources', group: 'files' },
];

const LABEL_BY_ID = new Map(IMPORT_SOURCES.map((s) => [s.id, s.label]));

export function importSourceLabel(id: ImportSourceId): string {
  return LABEL_BY_ID.get(id) ?? id;
}

export function isFileOnlySource(id: ImportSourceId): boolean {
  return IMPORT_SOURCES.find((s) => s.id === id)?.fileOnly ?? false;
}

/**
 * Account/registry-based sources with no on-disk connections file to browse
 * (their descriptor carries empty `fileExtensions`). The review dialog hides
 * its "Browse…" file-picker fallback for these — for `azure`, browsing would
 * run `parseFile()` which returns `[]` and wipe the picked candidates. ODBC is
 * deliberately excluded: it still has a real `odbc.ini` to browse.
 */
export function isFilelessSource(id: ImportSourceId): boolean {
  return id === 'azure';
}
